// `/accept-invite?token=…` — spec 15 §3.1 step 2, the screen the invite email's
// link lands on.
//
// WHAT THIS SCREEN IS CAREFUL ABOUT, explicitly:
//
//  1. THE TOKEN IS A CREDENTIAL. It arrives in the query string because the
//     email link puts it there (§3.1.1) and nothing here makes that worse: the
//     token is read once into a ref, never copied into state that renders,
//     never logged, never written to storage, never sent anywhere but
//     `POST /v1/platform/auth/accept-invite`. The address bar is CLEANED
//     (history.replaceState) the moment the token is read, so a shoulder, a
//     screenshot, or a browser-history sync no longer carries it.
//  2. NO ORACLE. Expired, already-used and never-existed all render the SAME
//     sentence, because the API deliberately answers the same way (§0.2) and a
//     UI that distinguished them would hand back exactly the signal the API
//     refuses to give.
//  3. THE SERVER IS THE AUTHORITY on password strength. The meter is guidance
//     (lib/password-guidance.ts); only the 12-character floor and the
//     confirm-match — the two things a browser can know — gate the button, and
//     a server refusal renders verbatim.

import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Banner } from "../../../components/toast";
import { Button } from "../../../components/button";
import { AdminAuthCard } from "../admin-shell";
import { acceptInvite, PlatformError } from "../lib/platform-api";
import { setAdminFlash } from "../lib/flash";
import {
  estimateStrength,
  PASSWORD_MIN_LENGTH,
  strengthSteps,
  type PasswordStrength,
} from "../lib/password-guidance";

/** Read the token out of the URL and scrub it from the address bar. */
function takeTokenFromUrl(): string {
  const url = new URL(window.location.href);
  const token = url.searchParams.get("token") ?? "";
  if (token !== "") {
    url.searchParams.delete("token");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}`);
  }
  return token;
}

const STRENGTH_KEY: Record<PasswordStrength, string> = {
  none: "admin.accept.strength.none",
  weak: "admin.accept.strength.weak",
  fair: "admin.accept.strength.fair",
  good: "admin.accept.strength.good",
  strong: "admin.accept.strength.strong",
};

export function AcceptInviteScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Read ONCE, on the first render, and keep it out of React state entirely —
  // state is what renders, and this must never be rendered.
  const tokenRef = useRef<string | null>(null);
  tokenRef.current ??= takeTokenFromUrl();
  const token = tokenRef.current;

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [touched, setTouched] = useState(false);

  const strength = estimateStrength(password);
  const tooShort = password.length > 0 && password.length < PASSWORD_MIN_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = token !== "" && password.length >= PASSWORD_MIN_LENGTH && confirm === password;

  const accept = useMutation({
    mutationFn: async () => {
      await acceptInvite({ token, password });
    },
    onSuccess: () => {
      // The password is dropped from memory with the screen; the next screen is
      // told only THAT it happened (lib/flash.ts — never what it was).
      setAdminFlash("password_set");
      void navigate({ to: "/login" });
    },
  });

  // NO unmount "scrub" of the password state, deliberately. An earlier draft
  // cleared both fields in an unmount cleanup and claimed that kept the typed
  // password from lingering. It does nothing of the sort: setting state on a
  // component React is already discarding is a no-op, and the state — like
  // every other string the render produced — is unreachable and collectable the
  // moment the tree unmounts. Security code that performs no security is worse
  // than none, because the comment above it stops anyone looking harder.
  const error = accept.error;
  const errorMessage = ((): string | undefined => {
    if (error === null) return undefined;
    if (!(error instanceof PlatformError)) return t("admin.accept.failed");
    // 401 STAFF_INVITE_INVALID and 410 STAFF_INVITE_EXPIRED get the SAME
    // sentence on purpose (§0.2 — no oracle about which one it was).
    if (error.status === 401 || error.status === 410) return t("admin.accept.invalidToken");
    if (error.code === "PASSWORD_POLICY") return t("admin.accept.weak");
    return t("admin.accept.failed");
  })();

  // NO TOKEN, NO FORM. The screen used to render the full password form under
  // the banner with a permanently disabled submit: someone whose mail client
  // mangled the query string got two fields to fill in, a strength meter
  // rewarding them for it, and a button that could never work — with no way
  // onward, while `/enroll`'s failure and the 404 both offer one. A form whose
  // submit is structurally impossible is not a form; it is a trap. This branch
  // states the situation and points at the only door that exists.
  if (token === "") {
    return (
      <AdminAuthCard
        title={t("admin.accept.title")}
        lead={t("admin.accept.lead")}
        data-testid="admin-accept"
      >
        <Banner kind="bad" data-testid="admin-accept-no-token">
          {t("admin.accept.missingToken")}
        </Banner>
        <p className="admin-card-lead">{t("admin.accept.missingTokenHelp")}</p>
        <div className="admin-actions">
          <Button
            variant="primary"
            onClick={() => {
              void navigate({ to: "/login" });
            }}
            data-testid="admin-accept-to-login"
          >
            {t("admin.accept.goSignIn")}
          </Button>
        </div>
      </AdminAuthCard>
    );
  }

  return (
    <AdminAuthCard
      title={t("admin.accept.title")}
      lead={t("admin.accept.lead")}
      data-testid="admin-accept"
    >
      {errorMessage !== undefined ? (
        <Banner kind="bad" data-testid="admin-accept-error">
          {errorMessage}
          {error instanceof PlatformError && error.requestId !== undefined ? (
            <span className="admin-reqid">
              {" "}
              {t("admin.accept.requestId")}: <code>{error.requestId}</code>
            </span>
          ) : null}
        </Banner>
      ) : null}
      <form
        className="admin-form"
        data-testid="admin-accept-form"
        onSubmit={(e) => {
          e.preventDefault();
          setTouched(true);
          if (!canSubmit) return;
          accept.mutate();
        }}
      >
        <div className="ds-field">
          <label htmlFor="admin-password">{t("admin.accept.password")}</label>
          <input
            id="admin-password"
            className="ds-input"
            type={reveal ? "text" : "password"}
            autoComplete="new-password"
            // The browser must not offer to remember a half-typed credential,
            // and no password manager heuristic should treat this as a search.
            spellCheck={false}
            autoCapitalize="off"
            value={password}
            data-testid="admin-password"
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            onBlur={() => {
              setTouched(true);
            }}
            aria-describedby="admin-password-hint admin-strength"
            required
          />
          <p className="admin-hint" id="admin-password-hint">
            {t("admin.accept.passwordHint")}
          </p>
          <div className="admin-strength" id="admin-strength" data-testid="admin-strength">
            <span className="admin-strength-lb">{t("admin.accept.strengthLabel")}</span>
            <span className="admin-strength-bar" aria-hidden="true">
              {[0, 1, 2, 3].map((i) => (
                <i key={i} className={i < strengthSteps(strength) ? "is-on" : undefined} />
              ))}
            </span>
            <span className="admin-strength-v" data-testid="admin-strength-word">
              {t(STRENGTH_KEY[strength])}
            </span>
          </div>
          <p className="admin-hint">{t("admin.accept.strengthNote")}</p>
          {touched && tooShort ? (
            <p className="admin-err" role="alert" data-testid="admin-too-short">
              {t("admin.accept.tooShort")}
            </p>
          ) : null}
        </div>

        <div className="ds-field">
          <label htmlFor="admin-password-confirm">{t("admin.accept.confirm")}</label>
          <input
            id="admin-password-confirm"
            className="ds-input"
            type={reveal ? "text" : "password"}
            autoComplete="new-password"
            spellCheck={false}
            autoCapitalize="off"
            value={confirm}
            data-testid="admin-password-confirm"
            onChange={(e) => {
              setConfirm(e.target.value);
            }}
            required
          />
          {mismatch ? (
            <p className="admin-err" role="alert" data-testid="admin-mismatch">
              {t("admin.accept.mismatch")}
            </p>
          ) : null}
        </div>

        <div className="admin-actions">
          <Button
            type="submit"
            variant="primary"
            busy={accept.isPending}
            disabled={!canSubmit}
            data-testid="admin-accept-submit"
          >
            {accept.isPending ? t("admin.accept.submitting") : t("admin.accept.submit")}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setReveal((v) => !v);
            }}
            data-testid="admin-reveal"
          >
            {reveal ? t("admin.accept.hide") : t("admin.accept.show")}
          </Button>
        </div>
      </form>
    </AdminAuthCard>
  );
}
